import { IUserInformation } from "models/page";
import {
  IMyAccountPageTranslation,
  MediaPickerDataTypes,
} from "models/translation";
import { AddOnEntitlements, PremiumEntitlements } from "shared-types";
import {
  checkIsFilmmakersProfile,
  doesUserHaveEntitlements,
} from "shared-auth";
import { agentsQuickLinks } from "pages/IndexPage/constants/agentsQuickLinks";
import {
  castingDirectorInactiveQuickLinks,
  getCastingQuickLinks,
} from "pages/IndexPage/constants/castingQuickLinks";
import {
  filmmakersQuickLinks,
  performersAddOnBundleQuickLinks,
  performersDefaultQuickLinks,
  performersGraduatesQuickLinks,
  performersInactiveQuickLinks,
  performersYPQuickLinks,
} from "pages/IndexPage/constants/performersQuickLinks";

export function isAccountStatusActive(userAccountStatus: string) {
  return (
    userAccountStatus === "current" ||
    userAccountStatus == "renewing" ||
    userAccountStatus == "coolingoff"
  );
}

export interface IQuickLink {
  text: string;
  href: string;
}

export function getQuickLinksForUser(
  userInfo: IUserInformation
): undefined | IQuickLink[] {
  const accountActive = isAccountStatusActive(userInfo.membershipStatus);
  const isPremierLEGACY = doesUserHaveEntitlements(
    PremiumEntitlements.Premium_Site_Access
  );
  const hasAddOnBundle = doesUserHaveEntitlements(
    AddOnEntitlements.AddOnBundle
  );
  const isFilmmakersProfile = checkIsFilmmakersProfile();

  const isEntitled = isPremierLEGACY || hasAddOnBundle;

  switch (userInfo.memberGroup) {
    case "castingDirector": {
      if (!accountActive) return castingDirectorInactiveQuickLinks;

      return getCastingQuickLinks();
    }
    case "performer": {
      if (!accountActive) return performersInactiveQuickLinks;

      if (isFilmmakersProfile) return filmmakersQuickLinks;

      if (userInfo.memberSubType == "youngPerformer")
        return performersYPQuickLinks;

      if (userInfo.memberSubType === "graduate")
        return performersGraduatesQuickLinks;

      if (isEntitled) return performersAddOnBundleQuickLinks;

      return performersDefaultQuickLinks;
    }
    case "agent":
      return agentsQuickLinks;
  }

  return undefined;
}

export type HtmlAnchorButton = {
  text: string;
  title: string;
  href: string;
  type?: "secondary" | "primary";
  target?: string;
  onClick?: () => void;
  className?: string;
};

export function getMarketingMessageForUser(
  userInfo: IUserInformation,
  translations: IMyAccountPageTranslation
): {
  marketingBannerHeading: string;
  marketingBannerBody: string;
  marketingBannerCallToActionUrl: string;
  marketingBannerBackgroundImage: MediaPickerDataTypes | undefined | null;
  marketingBannerCallToActionText: string;
} {
  const isPremierLEGACY = doesUserHaveEntitlements(
    PremiumEntitlements.Premium_Site_Access
  );
  const hasAddOnBundle = doesUserHaveEntitlements(
    AddOnEntitlements.AddOnBundle
  );
  const isEntitled = isPremierLEGACY || hasAddOnBundle;

  switch (userInfo.memberGroup) {
    case "agent":
      return {
        marketingBannerHeading:
          translations["marketing Banner - Agent"]
            ?.marketingBannerAgentHeading || "",
        marketingBannerBody:
          translations["marketing Banner - Agent"]?.marketingBannerAgentBody ||
          "",
        marketingBannerCallToActionUrl:
          translations["marketing Banner - Agent"]
            ?.marketingBannerAgentCallToActionUrl || "#",
        marketingBannerBackgroundImage:
          translations["marketing Banner - Agent"]
            ?.marketingBannerAgentBackgroundImage,
        marketingBannerCallToActionText:
          translations["marketing Banner - Agent"]
            ?.marketingBannerAgentCallToActionText || "",
      };
    case "castingDirector":
      return {
        marketingBannerHeading:
          translations["marketing Banner - Casting Director"]
            ?.marketingBannerCastingDirectorHeading || "",
        marketingBannerBody:
          translations["marketing Banner - Casting Director"]
            ?.marketingBannerCastingDirectorBody || "",
        marketingBannerCallToActionUrl:
          translations["marketing Banner - Casting Director"]
            ?.marketingBannerCastingDirectorCallToActionUrl || "#",
        marketingBannerBackgroundImage:
          translations["marketing Banner - Casting Director"]
            ?.marketingBannerCastingDirectorBackgroundImage,
        marketingBannerCallToActionText:
          translations["marketing Banner - Casting Director"]
            ?.marketingBannerCastingDirectorCallToActionText || "",
      };
    case "performer":
      if (userInfo.memberSubType === "youngPerformer")
        return {
          marketingBannerHeading:
            translations["marketing Banner - Young Performer"]
              ?.marketingBannerGraduateHeading || "",
          marketingBannerBody:
            translations["marketing Banner - Young Performer"]
              ?.marketingBannerGraduateBody || "",
          marketingBannerCallToActionUrl:
            translations["marketing Banner - Young Performer"]
              ?.marketingBannerGraduateCallToActionUrl || "#",
          marketingBannerBackgroundImage:
            translations["marketing Banner - Young Performer"]
              ?.marketingBannerGraduateBackgroundImage,
          marketingBannerCallToActionText:
            translations["marketing Banner - Young Performer"]
              ?.marketingBannerGraduateCallToActionText || "",
        };

      if (userInfo.memberSubType === "graduate") {
        return {
          marketingBannerHeading:
            translations["marketing Banner - Graduate"]
              ?.marketingBannerGraduateHeading || "",
          marketingBannerBody:
            translations["marketing Banner - Graduate"]
              ?.marketingBannerGraduateBody || "",
          marketingBannerCallToActionUrl:
            translations["marketing Banner - Graduate"]
              ?.marketingBannerGraduateCallToActionUrl || "#",
          marketingBannerBackgroundImage:
            translations["marketing Banner - Graduate"]
              ?.marketingBannerGraduateBackgroundImage,
          marketingBannerCallToActionText:
            translations["marketing Banner - Graduate"]
              ?.marketingBannerGraduateCallToActionText || "",
        };
      }

      if (isEntitled) {
        return {
          marketingBannerHeading:
            translations["marketing Banner - Performer (Add On - Bundle)"]
              ?.marketingBannerPerformerAddOnBundleHeading || "",
          marketingBannerBody:
            translations["marketing Banner - Performer (Add On - Bundle)"]
              ?.marketingBannerPerformerAddOnBundleBody || "",
          marketingBannerCallToActionUrl:
            translations["marketing Banner - Performer (Add On - Bundle)"]
              ?.marketingBannerPerformerAddOnBundleCallToActionUrl || "#",
          marketingBannerBackgroundImage:
            translations["marketing Banner - Performer (Add On - Bundle)"]
              ?.marketingBannerPerformerAddOnBundleBackgroundImage,
          marketingBannerCallToActionText:
            translations["marketing Banner - Performer (Add On - Bundle)"]
              ?.marketingBannerPerformerAddOnBundleCallToActionText || "",
        };
      }

    // eslint-disable-next-line no-fallthrough
    default:
      return {
        marketingBannerHeading:
          translations["marketing Banner - Performer"]
            ?.marketingBannerPerformerHeading || "",
        marketingBannerBody:
          translations["marketing Banner - Performer"]
            ?.marketingBannerPerformerBody || "",
        marketingBannerCallToActionUrl:
          translations["marketing Banner - Performer"]
            ?.marketingBannerPerformerCallToActionUrl || "#",
        marketingBannerBackgroundImage:
          translations["marketing Banner - Performer"]
            ?.marketingBannerPerformerBackgroundImage,
        marketingBannerCallToActionText:
          translations["marketing Banner - Performer"]
            ?.marketingBannerPerformerCallToActionText || "",
      };
  }
}

export function getServiceBannerForUser(
  userInfo: IUserInformation,
  translations: IMyAccountPageTranslation
): {
  serviceBannerHeading: string | undefined;
  serviceBannerMessage: string | undefined;
  serviceBannerMedia: MediaPickerDataTypes | undefined | null;
} {
  const isPremierLEGACY = doesUserHaveEntitlements(
    PremiumEntitlements.Premium_Site_Access
  );
  const hasAddOnBundle = doesUserHaveEntitlements(
    AddOnEntitlements.AddOnBundle
  );
  const isEntitled = isPremierLEGACY || hasAddOnBundle;
  const accountActive = isAccountStatusActive(userInfo.membershipStatus);
  switch (userInfo.memberGroup) {
    case "castingDirector":
      if (accountActive) {
        if (userInfo.memberSubType === "searchOnlyCastingDirector") {
          return {
            serviceBannerHeading:
              translations["service Banner - Casting Director (search only)"]
                ?.serviceBannerCastingDirectorSearchOnlyHeading,
            serviceBannerMessage:
              translations["service Banner - Casting Director (search only)"]
                ?.serviceBannerCastingDirectorSearchOnlyBody,
            serviceBannerMedia:
              translations["service Banner - Casting Director (search only)"]
                ?.serviceBannerCastingDirectorSearchOnlyMedia,
          };
        } else {
          return {
            serviceBannerHeading:
              translations["service Banner - Casting Director (default)"]
                ?.serviceBannerCastingDirectorDefaultHeading,
            serviceBannerMessage:
              translations["service Banner - Casting Director (default)"]
                ?.serviceBannerCastingDirectorDefaultBody,
            serviceBannerMedia:
              translations["service Banner - Casting Director (default)"]
                ?.serviceBannerCastingDirectorDefaultMedia,
          };
        }
      } else {
        return {
          serviceBannerHeading:
            translations["service Banner - Casting Director (inactive)"]
              ?.serviceBannerCastingDirectorInactiveHeading,
          serviceBannerMessage:
            translations["service Banner - Casting Director (inactive)"]
              ?.serviceBannerCastingDirectorInactiveBody,
          serviceBannerMedia:
            translations["service Banner - Casting Director (inactive)"]
              ?.serviceBannerCastingDirectorInactiveMedia,
        };
      }
    case "performer":
      if (accountActive) {
        if (userInfo.memberSubType === "graduate") {
          return {
            serviceBannerHeading:
              translations["service Banner - Graduate"]
                ?.serviceBannerGraduateHeading,
            serviceBannerMessage:
              translations["service Banner - Graduate"]
                ?.serviceBannerGraduateBody,
            serviceBannerMedia:
              translations["service Banner - Graduate"]
                ?.serviceBannerGraduateMedia,
          };
        }

        if (userInfo.memberSubType === "youngPerformer") {
          return {
            serviceBannerHeading:
              translations["service Banner - Young Performer"]
                ?.serviceBannerPerformerYoungHeading,
            serviceBannerMessage:
              translations["service Banner - Young Performer"]
                ?.serviceBannerPerformerYoungBody,
            serviceBannerMedia:
              translations["service Banner - Young Performer"]
                ?.serviceBannerPerformerYoungMedia,
          };
        }

        if (isEntitled) {
          return {
            serviceBannerHeading:
              translations["service Banner - Performer (Add On - Bundle)"]
                ?.serviceBannerPerformerAddOnBundleHeading,
            serviceBannerMessage:
              translations["service Banner - Performer (Add On - Bundle)"]
                ?.serviceBannerPerformerAddOnBundleBody,
            serviceBannerMedia:
              translations["service Banner - Performer (Add On - Bundle)"]
                ?.serviceBannerPerformerAddOnBundleMedia,
          };
        }

        return {
          serviceBannerHeading:
            translations["service Banner - Performer (default)"]
              ?.serviceBannerPerformerDefaultHeading,
          serviceBannerMessage:
            translations["service Banner - Performer (default)"]
              ?.serviceBannerPerformerDefaultBody,
          serviceBannerMedia:
            translations["service Banner - Performer (default)"]
              ?.serviceBannerPerformerDefaultMedia,
        };
      } else {
        return {
          serviceBannerHeading:
            translations["service Banner - Performer (inactive)"]
              ?.serviceBannerPerformerInactiveHeading,
          serviceBannerMessage:
            translations["service Banner - Performer (inactive)"]
              ?.serviceBannerPerformerInactiveBody,
          serviceBannerMedia:
            translations["service Banner - Performer (inactive)"]
              ?.serviceBannerPerformerInactiveMedia,
        };
      }
    case "agent":
      return {
        serviceBannerHeading:
          translations["service Banner - Agent"]?.serviceBannerAgentHeading,
        serviceBannerMessage:
          translations["service Banner - Agent"]?.serviceBannerAgentBody,
        serviceBannerMedia:
          translations["service Banner - Agent"]?.serviceBannerAgentMedia,
      };
  }

  return {
    serviceBannerHeading: undefined,
    serviceBannerMessage: undefined,
    serviceBannerMedia: undefined,
  };
}
